/**
 * Findings Utilities
 * 
 * Generic utilities for copying and managing findings across modules.
 * Includes metadata enrichment for AI quality, audit workflow, and data quality.
 * 
 * Moved from tax-account-config.ts to enable multi-module support.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { UserRole } from '@/lib/rolePermissions';

type AuditSeverity = Database['public']['Enums']['audit_severity'];

// ============= Metadata Enrichment Helpers =============

/**
 * Determine if a finding is ready for audit report inclusion
 * Based on confidence, hypothesis status, and severity review requirements
 */
export function determineAuditReady(finding: any): boolean {
  // Not ready if it's a hypothesis
  if (finding.is_hypothesis) return false;
  
  // Critical findings need explicit review before audit-ready
  if (finding.severity === 'critical' && !finding.reviewed) return false;
  
  // Ready if confidence is high enough (default to 1.0 for deterministic rules)
  const confidence = finding.confidence ?? finding.ai_confidence ?? 1.0;
  return confidence >= 0.7;
}

/**
 * Determine if a finding requires partner/senior review
 * Based on severity and confidence thresholds
 */
export function determinePartnerReview(finding: any): boolean {
  // Critical severity always needs partner review
  if (finding.severity === 'critical') return true;
  
  // Low confidence needs partner review
  const confidence = finding.confidence ?? finding.ai_confidence;
  if (confidence !== undefined && confidence < 0.5) return true;
  
  // Hypotheses need review
  if (finding.is_hypothesis) return true;
  
  return false;
}

/**
 * Determine minimum role required to resolve a finding
 * Based on severity - higher severity needs higher role
 */
export function determineMinRoleToResolve(finding: any): UserRole {
  const severity = finding.severity?.toLowerCase() || 'medium';
  
  if (severity === 'critical') return 'admin';
  if (severity === 'high') return 'moderator';
  return 'user';
}

/**
 * Determine minimum role required to view a finding
 * Most findings are visible to all, but low-confidence/hypothesis may be restricted
 */
export function determineMinRoleToView(finding: any): UserRole {
  // Hypotheses require at least moderator to see
  if (finding.is_hypothesis) return 'moderator';
  
  // Very low confidence requires moderator
  const confidence = finding.confidence ?? finding.ai_confidence ?? 1.0;
  if (confidence < 0.4) return 'moderator';
  
  // Default: visible to all users
  return 'user';
}

/**
 * Copy findings from a completed revision to the audit_findings table.
 * This enables the "Åtgärder" tab to display findings.
 * 
 * @param revisionId - The revision ID to copy findings from
 * @param auditId - The audit ID to copy findings to
 * @param moduleId - The module ID (e.g., 'module_5', 'module_6')
 * @param moduleType - The module type (e.g., 'taxes_fees', 'revenue')
 */
export async function copyRevisionFindingsToAudit(
  revisionId: string,
  auditId: string,
  moduleId?: string,
  moduleType?: string
): Promise<{ copiedCount: number; error?: string }> {
  console.log('[copyRevisionFindingsToAudit] Starting copy:', { revisionId, auditId, moduleId, moduleType });
  
  try {
    // STEG 1: Rensa ALLA befintliga findings för denna audit + modul
    // Detta förhindrar ackumulering av gamla findings
    const effectiveModuleId = moduleId || moduleType || null;
    if (effectiveModuleId) {
      // Get all module aliases for backward compatibility
      const { getModuleAliases } = await import('./index')
      const moduleAliases = getModuleAliases(effectiveModuleId)
      const moduleIdFilter = moduleAliases.map(a => `module_id.eq.${a}`).join(',')
      
      console.log(`[copyRevisionFindingsToAudit] Deleting old findings for audit=${auditId}, module=${effectiveModuleId} (aliases: ${moduleAliases.join(', ')})`);
      const { error: deleteError } = await supabase
        .from('audit_findings')
        .delete()
        .eq('audit_id', auditId)
        .or(moduleIdFilter);
      
      if (deleteError) {
        console.warn('[copyRevisionFindingsToAudit] Warning - could not delete old findings:', deleteError);
        // Fortsätt ändå - bättre att ha dubbletter än att missa nya findings
      } else {
        console.log(`[copyRevisionFindingsToAudit] Deleted old findings for module ${effectiveModuleId}`);
      }
    }

    // STEG 2: Fetch completed batches for this revision
    const { data: batches, error: batchError } = await supabase
      .from('batches')
      .select('id, result_json')
      .eq('revision_id', revisionId)
      .eq('state', 'completed');

    if (batchError) {
      console.error('[copyRevisionFindingsToAudit] Failed to fetch batches:', batchError);
      return { copiedCount: 0, error: batchError.message };
    }

    console.log(`[copyRevisionFindingsToAudit] Found ${batches?.length || 0} completed batches`);

    if (!batches || batches.length === 0) {
      console.log('[copyRevisionFindingsToAudit] No completed batches found');
      return { copiedCount: 0 };
    }

    // STEG 3: Extract findings from all batches
    const allFindings: any[] = [];
    for (const batch of batches) {
      const resultJson = batch.result_json as any;
      if (resultJson?.findings && Array.isArray(resultJson.findings)) {
        allFindings.push(...resultJson.findings.map((f: any) => ({
          ...f,
          batchId: batch.id,
        })));
      }
    }

    console.log(`[copyRevisionFindingsToAudit] Extracted ${allFindings.length} findings from batches`);

    if (allFindings.length === 0) {
      console.log('[copyRevisionFindingsToAudit] No findings to copy');
      return { copiedCount: 0 };
    }

    // STEG 4: Eftersom vi redan rensade gamla findings, behöver vi inte kolla för dubbletter
    // men vi behåller en snabb check för säkerhets skull
    const { count: existingForRevision } = await supabase
      .from('audit_findings')
      .select('id', { count: 'exact', head: true })
      .eq('audit_id', auditId)
      .eq('run_id', revisionId);

    if (existingForRevision && existingForRevision > 0) {
      console.log(`[copyRevisionFindingsToAudit] Findings already exist for revision ${revisionId}, skipping`);
      return { copiedCount: 0 };
    }

    // STEG 5: Prepare new findings for insert with enriched metadata
    const effectiveModuleType = moduleType || moduleId || 'unknown';
    const newFindings = allFindings.map(finding => ({
      // Core fields
      audit_id: auditId,
      module_id: moduleId || null,
      run_id: revisionId,
      severity: mapSeverityToAuditSeverity(finding.severity || 'medium'),
      title: generateFindingTitle(finding),
      description: finding.message || finding.description || 'Ingen beskrivning',
      finding_type: finding.findingType || finding.finding_type || finding.type || finding.category || 'general',
      flagged_by_model: finding.source || 'batch-analysis',
      status: 'open',
      
      // AI Quality Metadata
      ai_confidence: finding.confidence ?? finding.ai_confidence ?? null,
      is_hypothesis: finding.is_hypothesis ?? false,
      prompt_version: finding.prompt_version ?? null,
      
      // Audit Workflow Metadata (computed from finding data)
      audit_ready: determineAuditReady(finding),
      requires_partner_review: determinePartnerReview(finding),
      min_role_to_view: determineMinRoleToView(finding),
      min_role_to_resolve: determineMinRoleToResolve(finding),
      
      // Data Quality Context
      data_quality_score: finding.data_quality_score ?? null,
      data_quality_issues: finding.data_quality_issues ?? [],
      
      // Cross-module Relations (empty by default, can be populated later)
      related_finding_ids: [],
      depends_on_modules: [],
      
      // Original data reference
      data_reference: {
        module_type: effectiveModuleType,
        original_finding: finding,
        finding_hash: finding.message || JSON.stringify(finding),
        batch_id: finding.batchId,
        revision_id: revisionId,
      },
    }));

    console.log(`[copyRevisionFindingsToAudit] Prepared ${newFindings.length} new findings for insert`);

    // Insert new findings
    const { error: insertError } = await supabase
      .from('audit_findings')
      .insert(newFindings);

    if (insertError) {
      console.error('[copyRevisionFindingsToAudit] Insert error:', insertError);
      return { copiedCount: 0, error: insertError.message };
    }

    console.log(`[copyRevisionFindingsToAudit] Successfully copied ${newFindings.length} findings`);
    return { copiedCount: newFindings.length };
  } catch (err: any) {
    console.error('[copyRevisionFindingsToAudit] Unexpected error:', err);
    return { copiedCount: 0, error: err.message };
  }
}

/**
 * Generate a human-readable title for a finding
 */
export function generateFindingTitle(finding: any): string {
  const type = finding.type || finding.category || '';
  
  switch (type) {
    case 'late_payment':
      return `Försenad inbetalning: ${finding.daysLate || '?'} dagar sen`;
    case 'amount_deviation':
      return `Beloppsavvikelse: ${finding.amount || '?'} kr`;
    case 'interest_charge':
      return `Kostnadsränta debiterad`;
    case 'penalty_fee':
      return `Förseningsavgift`;
    case 'irregular_pattern':
      return `Oregelbundet mönster upptäckt`;
    case 'missing_declaration':
      return `Saknad deklaration`;
    default:
      return finding.title || finding.message?.substring(0, 50) || 'Avvikelse upptäckt';
  }
}

/**
 * Map finding severity to audit_severity enum
 */
export function mapSeverityToAuditSeverity(severity: string): AuditSeverity {
  const normalized = severity?.toLowerCase() || 'medium';
  
  if (['critical', 'high', 'hög'].includes(normalized)) {
    return 'high';
  }
  if (['medium', 'warning', 'medel'].includes(normalized)) {
    return 'medium';
  }
  if (['low', 'info', 'låg'].includes(normalized)) {
    return 'low';
  }
  
  return 'medium';
}

/**
 * Extract consecutive days from a late_payment finding description.
 * Used for tolerance filtering across the app.
 * 
 * @param finding - Finding object with findingType/finding_type and description
 * @returns Number of consecutive days, or null if not applicable
 */
export function extractDaysFromFinding(finding: { 
  findingType?: string; 
  finding_type?: string; 
  description?: string;
}): number | null {
  const type = finding.findingType || finding.finding_type;
  if (type !== 'late_payment' && type !== 'general') {
    return null; // Not a late payment finding
  }
  
  const description = finding.description || '';
  // Match patterns like "2 konsekutiva dagar" or "4 konsekutiv dag"
  const match = description.match(/(\d+)\s*konsekutiva?\s*dag/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Get all findings for a specific audit and module
 */
export async function getAuditFindings(
  auditId: string,
  moduleId?: string
): Promise<{ findings: any[]; error?: string }> {
  try {
    let query = supabase
      .from('audit_findings')
      .select('*')
      .eq('audit_id', auditId)
      .order('created_at', { ascending: false });

    if (moduleId) {
      // Get all module aliases for backward compatibility
      const { getModuleAliases } = await import('./index')
      const moduleAliases = getModuleAliases(moduleId)
      const moduleIdFilter = moduleAliases.map(a => `module_id.eq.${a}`).join(',')
      query = query.or(`${moduleIdFilter},module_id.is.null`);
    }

    const { data, error } = await query;

    if (error) {
      return { findings: [], error: error.message };
    }

    return { findings: data || [] };
  } catch (err: any) {
    return { findings: [], error: err.message };
  }
}
